import { useQueries } from '@tanstack/react-query'
import { Mutex } from 'async-mutex'
import { useEffect, useState } from 'react'

// This is fine since it's only 3 instances throughout the whole app atm
const mutexMap = new Map<string, Mutex>()

function getMutex(key: string): Mutex {
	let mutex = mutexMap.get(key)
	if (!mutex) {
		mutex = new Mutex()
		mutexMap.set(key, mutex)
	}
	return mutex
}

/**
 * Like useQueries but ensures queries execute in array order.
 * Different serialKeys run in parallel; same key runs sequentially by index.
 * @param options.serialKey Key to group queries. MUST be a static string constant.
 */
export function useSerialQueries<T extends any[]>(options: {
	queries: T
	serialKey: string
}): ReturnType<typeof useQueries<T>> {
	const mutex = getMutex(options.serialKey)
	const [enabledUpTo, setEnabledUpTo] = useState(0)

	const results = useQueries({
		queries: options.queries.map((query, index) => ({
			...query,
			enabled: (query.enabled ?? true) && index <= enabledUpTo,
			queryFn: query.queryFn
				? async (ctx: { signal: AbortSignal }) => {
						return mutex.runExclusive(() => {
							if (ctx.signal.aborted) {
								throw new DOMException('Aborted', 'AbortError')
							}
							return query.queryFn(ctx)
						})
					}
				: undefined,
		})),
	})

	const currentPending = results[enabledUpTo]?.isPending

	useEffect(() => {
		if (currentPending === false && enabledUpTo < options.queries.length - 1) {
			setEnabledUpTo((prev) => prev + 1)
		}
	}, [currentPending, enabledUpTo, options.queries.length])

	return results as ReturnType<typeof useQueries<T>>
}
